import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DateRangeFilter, buildQuery, useDateRange, useReport } from "@/components/reports/DateRangeFilter";
import {
  ColumnChart,
  DataTable,
  SectionCard,
  StatTile,
  TargetBar,
  fmtDate,
  fmtMoney,
  fmtMonth,
  fmtNum,
  fmtPct,
} from "@/components/reports/ReportWidgets";
import type { ReportUser, UserMetrics } from "@/types/report";
import { AreaTrendChart } from "@/components/charts/AreaTrendChart";
import { RadialTargetGauge } from "@/components/charts/RadialTargetGauge";

interface UserDetail {
  user: ReportUser;
  metrics: UserMetrics;
  trend: Array<{ period: string; deals_won: number; won_amount: number }>;
  open_deals: Array<{ id: string; name: string; amount: number; expected_close_date: string; stage_name: string; account_name: string; updated_on: string }>;
  open_leads: Array<{ id: string; name: string; company_name: string; lead_status: string; priority: string; score: number; created_on: string; last_contacted_at: string }>;
  overdue_tasks: Array<{ id: string; title: string; related_type: string; related_id: string; related_name: string; due_at: string }>;
  recent_activities: Array<{ id: string; activity_type: string; title: string; created_on: string; deal_id: string; deal_name: string }>;
  recent_quotes: Array<{ id: string; display_number: string; quote_number: string; quote_status: string; grand_total: number; max_discount_pct: number; created_on: string }>;
}

export function UserReportPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { preset, range, choosePreset, setRange } = useDateRange("this_month");
  const { data, loading, error } = useReport<UserDetail>(id ? `/api/Report/UserDetail/${id}${buildQuery({ from: range.from, to: range.to })}` : null);

  const m = data?.metrics;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} aria-label="Back">
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h2 className="text-xl font-bold tracking-tight text-foreground flex items-center gap-2">
              {data?.user.name || "Loading…"}
              {data && <Badge variant="outline" className="text-[10px]">{data.user.role_name}</Badge>}
            </h2>
            {data && (
              <p className="text-sm text-muted-foreground">
                {data.user.email}
                {data.user.manager_name && <> · reports to {data.user.manager_name}</>}
              </p>
            )}
          </div>
        </div>
        <DateRangeFilter preset={preset} range={range} onPreset={choosePreset} onRange={setRange} />
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {loading && !data && (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-primary" />
        </div>
      )}

      {data && m && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatTile label="Revenue won" value={fmtMoney(m.won_amount)} hint={<span>{m.deals_won} deals · avg {fmtMoney(m.avg_deal_size)}</span>} />
            <StatTile
              label="Target"
              value={m.target_achieved_pct === null ? "—" : fmtPct(m.target_achieved_pct)}
              hint={m.target_amount ? <TargetBar achieved={m.won_amount} target={m.target_amount} /> : <span>No target for this period</span>}
            />
            <StatTile label="Lead conversion" value={fmtPct(m.conversion_pct)} hint={<span>{m.leads_converted} of {m.leads_assigned} leads · {m.leads_untouched} untouched</span>} />
            <StatTile label="Win rate" value={fmtPct(m.win_rate_pct)} hint={<span>{m.deals_won} won · {m.deals_lost} lost · {m.avg_cycle_days}d cycle</span>} />
            <StatTile label="Open pipeline" value={fmtMoney(m.pipeline_amount)} hint={<span>{m.open_deals} deals · {fmtMoney(m.weighted_amount)} weighted</span>} />
            <StatTile label="Quotes" value={fmtNum(m.quotes_sent)} hint={<span>sent · {m.quotes_accepted} accepted · {fmtPct(m.avg_discount_pct)} avg disc.</span>} />
            <StatTile label="Activities" value={fmtNum(m.calls + m.meetings + m.emails + m.whatsapp + m.notes)} hint={<span>{m.calls} calls · {m.meetings} meetings</span>} />
            <StatTile label="Tasks" value={fmtNum(m.tasks_done)} hint={<span>done · {m.tasks_overdue} overdue now</span>} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <SectionCard title="Revenue won trend" description="Last 6 months monthly won deal value" className="lg:col-span-2">
              <AreaTrendChart
                data={data.trend.map((t) => ({
                  period: t.period,
                  label: fmtMonth(t.period),
                  value: t.won_amount,
                }))}
                formatValue={(v) => fmtMoney(v)}
                color="#2563eb"
                height={200}
              />
            </SectionCard>

            <SectionCard title="Target achievement" description="Monthly revenue quota pacing">
              <RadialTargetGauge
                achieved={m.won_amount}
                target={m.target_amount}
                label="Monthly Target"
                formatMoney={(v) => fmtMoney(v)}
                size={180}
              />
            </SectionCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <SectionCard title="Open deals" description="Largest first">
              <DataTable
                columns={[
                  { key: "name", header: "Deal", render: (r) => <Link to={`/deals/${r.id}`} className="font-medium text-primary hover:underline">{r.name}</Link> },
                  { key: "stage_name", header: "Stage" },
                  { key: "expected_close_date", header: "Expected", render: (r) => fmtDate(r.expected_close_date) },
                  { key: "amount", header: "Amount", align: "right", render: (r) => fmtMoney(r.amount) },
                ]}
                rows={data.open_deals}
                rowKey={(r) => r.id}
                emptyMessage="No open deals."
              />
            </SectionCard>
            <SectionCard title="Open leads" description="Newest first">
              <DataTable
                columns={[
                  { key: "name", header: "Lead", render: (r) => <span className="font-medium text-foreground">{r.name || r.company_name}</span> },
                  { key: "lead_status", header: "Status" },
                  { key: "score", header: "Score", align: "right" },
                  { key: "last_contacted_at", header: "Last contact", render: (r) => fmtDate(r.last_contacted_at) },
                ]}
                rows={data.open_leads}
                rowKey={(r) => r.id}
                emptyMessage="No open leads."
              />
            </SectionCard>
            <SectionCard title="Overdue tasks">
              <DataTable
                columns={[
                  { key: "title", header: "Task", render: (r) => <span className="font-medium text-foreground">{r.title}</span> },
                  { key: "related_name", header: "For", render: (r) => r.related_name || "—" },
                  { key: "due_at", header: "Due", render: (r) => fmtDate(r.due_at) },
                ]}
                rows={data.overdue_tasks}
                rowKey={(r) => r.id}
                emptyMessage="No overdue tasks."
              />
            </SectionCard>
            <SectionCard title="Recent activity">
              <DataTable
                columns={[
                  { key: "created_on", header: "When", render: (r) => fmtDate(r.created_on) },
                  { key: "activity_type", header: "Type", render: (r) => r.activity_type.toLowerCase() },
                  { key: "title", header: "Title", render: (r) => <span className="text-foreground">{r.title}</span> },
                  { key: "deal_name", header: "Deal", render: (r) => (r.deal_id ? <Link to={`/deals/${r.deal_id}`} className="text-primary hover:underline">{r.deal_name}</Link> : "—") },
                ]}
                rows={data.recent_activities}
                rowKey={(r) => r.id}
                emptyMessage="No activity logged."
              />
            </SectionCard>
            <SectionCard title="Recent quotes" className="lg:col-span-2">
              <DataTable
                columns={[
                  { key: "display_number", header: "Quote", render: (r) => <Link to={`/quotes/${r.id}`} className="font-medium text-primary hover:underline">{r.display_number || r.quote_number}</Link> },
                  { key: "quote_status", header: "Status", render: (r) => r.quote_status.replace(/_/g, " ").toLowerCase() },
                  { key: "created_on", header: "Created", render: (r) => fmtDate(r.created_on) },
                  { key: "max_discount_pct", header: "Max disc.", align: "right", render: (r) => fmtPct(r.max_discount_pct) },
                  { key: "grand_total", header: "Total", align: "right", render: (r) => fmtMoney(r.grand_total) },
                ]}
                rows={data.recent_quotes}
                rowKey={(r) => r.id}
                emptyMessage="No quotes."
              />
            </SectionCard>
          </div>
        </>
      )}
    </div>
  );
}
